import { Box, EllipsisText, Flex } from '@shared/components';

import {
  CELL_DIVIDER_SX,
  COLUMN_LABEL_ROW_HEIGHT,
  COLUMN_WIDTHS,
  DATA_ROW_HEIGHT,
  EMPTY_MESSAGE_ROW_HEIGHT,
  getTopBandColors,
  getTopBandDividerColor,
  GROUP_DIVIDER_SX,
  GROUP_WIDTH,
  METRIC_COLUMNS,
  METRIC_GROUPS,
  OUTER_BORDER_COLOR,
  ROW_DIVIDER_SX,
  TITLE_WIDTH,
  TOP_BAND_ROW_HEIGHT,
} from './columns';
import MetricGroupCells from './metric-group-cells';
import {
  type QuestionnaireGroupedMetrics,
  type QuestionnaireOptionRow,
  type QuestionnaireReportTableVariant,
} from './types';

interface QuestionnaireReportTableProps {
  title: string;
  rows: QuestionnaireOptionRow[];
  total?: QuestionnaireGroupedMetrics;
  variant?: QuestionnaireReportTableVariant;
  /**
   * Whether this table renders as a self-contained bordered card with its
   * own horizontal scroll. Set to `false` when the table is nested inside a
   * group container that owns the border and scroll (e.g. section 14). The
   * table becomes a naked flex column of rows and lets its parent handle
   * chrome + scrolling so columns align across sibling tables.
   *
   * @default true
   */
  standalone?: boolean;
  /**
   * When `true`, every metric cell (Count, Pass, Fail, Passing Rate)
   * across all data rows and the Total row renders a pulsing `<Skeleton>`
   * placeholder instead of the formatted value. Non-metric cells (`#`, option
   * label, Total label) and structural bands (title, column labels) are not
   * affected, keeping the layout pixel-stable between loading and loaded states.
   *
   * @default false
   */
  loading?: boolean;
  /**
   * Message rendered in the table body — in place of data rows and the Total
   * row — when the table has loaded with no rows (`rows` empty and `loading`
   * false). The title band and column labels stay visible so the section reads
   * as "loaded, but no data" rather than disappearing. When omitted, an empty,
   * non-loading table simply shows its title band. Ignored while `loading`.
   */
  emptyMessage?: string;
}

const CELL_PADDING_X = 1.5;
const cellBaseSx = {
  px: CELL_PADDING_X,
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  ...CELL_DIVIDER_SX,
} as const;

const rowBaseSx = { alignItems: 'stretch', flexShrink: 0, width: 'max-content' } as const;

export default function QuestionnaireReportTable(props: Readonly<QuestionnaireReportTableProps>) {
  const {
    title,
    rows,
    total,
    variant = 'standard',
    standalone = true,
    loading = false,
    emptyMessage,
  } = props;

  const topBand = getTopBandColors(variant);
  const topBandDividerColor = getTopBandDividerColor(variant);
  const topBandDividerSx = { borderLeft: `1px solid ${topBandDividerColor}` };
  // A loaded, empty table shows its empty message (when provided) in place of
  // the data rows and Total. While loading, `rows` may momentarily be empty —
  // don't treat that as the empty state.
  const showEmptyMessage = !loading && rows.length === 0 && emptyMessage != null;
  const hasBody = rows.length > 0 || total !== undefined || showEmptyMessage;

  return (
    <Box
      role='table'
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        width: 'fit-content',
        ...theme.typography.body2,
        ...(standalone
          ? {
              border: `1px solid ${OUTER_BORDER_COLOR}`,
              borderRadius: theme.shape.rounded(2),
              overflow: 'hidden',
              maxWidth: '100%',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }
          : {}),
      })}
    >
      {/* Top band — title, plus three group labels when the table has a body */}
      <Flex
        role='row'
        sx={{
          height: TOP_BAND_ROW_HEIGHT,
          ...rowBaseSx,
          ...ROW_DIVIDER_SX,
          backgroundColor: topBand.backgroundColor,
          color: topBand.color,
          fontWeight: 600,
        }}
      >
        <Box
          role='columnheader'
          sx={{
            ...cellBaseSx,
            width: hasBody ? TITLE_WIDTH : TITLE_WIDTH + GROUP_WIDTH * METRIC_GROUPS.length,
            height: TOP_BAND_ROW_HEIGHT,
            minWidth: 0,
            borderRight: 'none',
          }}
        >
          <EllipsisText component='span' sx={{ fontWeight: 600, color: 'inherit', width: '100%' }}>
            {title}
          </EllipsisText>
        </Box>
        {hasBody
          ? METRIC_GROUPS.map((group) => (
              <Box
                key={group.key}
                role='columnheader'
                sx={{
                  ...cellBaseSx,
                  ...topBandDividerSx,
                  width: GROUP_WIDTH,
                  height: TOP_BAND_ROW_HEIGHT,
                  justifyContent: 'center',
                  borderRight: 'none',
                }}
              >
                {group.label}
              </Box>
            ))
          : null}
      </Flex>

      {hasBody ? (
        <>
          {/* Column labels row */}
          <Flex
            role='row'
            sx={{
              height: COLUMN_LABEL_ROW_HEIGHT,
              ...rowBaseSx,
              ...ROW_DIVIDER_SX,
              backgroundColor: '#e5e5e5',
              color: 'text.primary',
              fontWeight: 600,
            }}
          >
            <Box
              role='columnheader'
              sx={{
                ...cellBaseSx,
                width: COLUMN_WIDTHS.index,
                height: COLUMN_LABEL_ROW_HEIGHT,
                justifyContent: 'center',
              }}
            >
              #
            </Box>
            <Box
              role='columnheader'
              sx={{
                ...cellBaseSx,
                width: COLUMN_WIDTHS.option,
                height: COLUMN_LABEL_ROW_HEIGHT,
                justifyContent: 'flex-start',
                borderRight: 'none',
              }}
            >
              Option
            </Box>
            {METRIC_GROUPS.map((group, groupIndex) => {
              const isLastGroup = groupIndex === METRIC_GROUPS.length - 1;
              const lastCellIndex = METRIC_COLUMNS.length - 1;
              return (
                <Flex key={group.key} role='presentation' sx={{ flexShrink: 0 }}>
                  {METRIC_COLUMNS.map((col, index) => (
                    <Box
                      key={col.key}
                      role='columnheader'
                      sx={{
                        ...cellBaseSx,
                        width: col.width,
                        height: COLUMN_LABEL_ROW_HEIGHT,
                        justifyContent: 'center',
                        ...(index === 0 ? GROUP_DIVIDER_SX : {}),
                        ...(index === lastCellIndex && !isLastGroup ? { borderRight: 'none' } : {}),
                      }}
                    >
                      {col.label}
                    </Box>
                  ))}
                </Flex>
              );
            })}
          </Flex>

          {/* Data rows */}
          {rows.map((row, index) => {
            const isEven = index % 2 === 0;
            return (
              <Flex
                key={row.id}
                role='row'
                sx={{
                  height: DATA_ROW_HEIGHT,
                  ...rowBaseSx,
                  ...ROW_DIVIDER_SX,
                  backgroundColor: isEven ? '#ffffff' : '#fafafa',
                }}
              >
                <Box
                  role='cell'
                  sx={{
                    ...cellBaseSx,
                    width: COLUMN_WIDTHS.index,
                    height: DATA_ROW_HEIGHT,
                    justifyContent: 'center',
                    color: 'text.secondary',
                  }}
                >
                  {index + 1}
                </Box>
                <Box
                  role='cell'
                  sx={{
                    ...cellBaseSx,
                    width: COLUMN_WIDTHS.option,
                    height: DATA_ROW_HEIGHT,
                    justifyContent: 'flex-start',
                    minWidth: 0,
                    borderRight: 'none',
                  }}
                >
                  <EllipsisText component='span' sx={{ color: 'inherit', width: '100%' }}>
                    {row.label}
                  </EllipsisText>
                </Box>
                <MetricGroupCells
                  metric={row.metrics.combined}
                  height={DATA_ROW_HEIGHT}
                  loading={loading}
                  isLastGroup={false}
                />
                <MetricGroupCells
                  metric={row.metrics.public}
                  height={DATA_ROW_HEIGHT}
                  loading={loading}
                  isLastGroup={false}
                />
                <MetricGroupCells
                  metric={row.metrics.inHouse}
                  height={DATA_ROW_HEIGHT}
                  loading={loading}
                />
              </Flex>
            );
          })}

          {/* Empty message — replaces data rows + Total when loaded with no rows */}
          {showEmptyMessage ? (
            <Flex
              role='row'
              sx={(theme) => ({
                height: EMPTY_MESSAGE_ROW_HEIGHT,
                ...rowBaseSx,
                backgroundColor: theme.palette.background.paper,
              })}
            >
              <Box
                role='cell'
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  width: TITLE_WIDTH + GROUP_WIDTH * METRIC_GROUPS.length,
                  height: EMPTY_MESSAGE_ROW_HEIGHT,
                  // Align the message with the Option column: index-column width
                  // plus the shared horizontal cell padding.
                  pl: `calc(${COLUMN_WIDTHS.index}px + ${theme.spacing(CELL_PADDING_X)})`,
                  color: 'text.primary',
                })}
              >
                {emptyMessage}
              </Box>
            </Flex>
          ) : null}

          {/* Total row */}
          {total !== undefined && !showEmptyMessage ? (
            <Flex
              role='row'
              sx={{
                height: DATA_ROW_HEIGHT,
                ...rowBaseSx,
                backgroundColor: '#fff7eb',
                fontWeight: 600,
              }}
            >
              <Box
                role='cell'
                sx={{
                  ...cellBaseSx,
                  width: COLUMN_WIDTHS.index,
                  height: DATA_ROW_HEIGHT,
                }}
              />
              <Box
                role='cell'
                sx={{
                  ...cellBaseSx,
                  width: COLUMN_WIDTHS.option,
                  height: DATA_ROW_HEIGHT,
                  justifyContent: 'flex-start',
                  fontWeight: 600,
                  borderRight: 'none',
                }}
              >
                Total
              </Box>
              <MetricGroupCells
                metric={total.combined}
                height={DATA_ROW_HEIGHT}
                fontWeight={600}
                loading={loading}
                isLastGroup={false}
              />
              <MetricGroupCells
                metric={total.public}
                height={DATA_ROW_HEIGHT}
                fontWeight={600}
                loading={loading}
                isLastGroup={false}
              />
              <MetricGroupCells
                metric={total.inHouse}
                height={DATA_ROW_HEIGHT}
                fontWeight={600}
                loading={loading}
              />
            </Flex>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
